import React, { useMemo, useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AdminPageLayout, AdminPageHeader } from "../../common/AdminPageLayout";
import { ADMIN_PAGE_META } from "../../common/adminPageMeta";
import { fetchDashboardSummary } from "./dashboardService";

const formatCurrency = (amount) => {
  const n = Number(amount);
  if (Number.isNaN(n)) return "—";
  return n.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });
};

const formatInt = (n) => {
  const v = Number(n);
  if (Number.isNaN(v)) return "—";
  return v.toLocaleString("en-IN");
};

const formatWhen = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
};

const PERIOD_TABS = [
  { id: "today", label: "Today", hint: "Since midnight" },
  { id: "month", label: "This month", hint: "Month to date" },
  { id: "year", label: "This year", hint: "Year to date" },
];

const HERO_ACCENTS = {
  blue: {
    wash: "bg-sky-50/70",
    stripe: "bg-gradient-to-b from-sky-500 to-blue-600",
    icon: "bg-gradient-to-br from-sky-500 to-blue-600 shadow-sky-500/30",
  },
  emerald: {
    wash: "bg-emerald-50/70",
    stripe: "bg-gradient-to-b from-emerald-500 to-teal-600",
    icon: "bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/30",
  },
  amber: {
    wash: "bg-amber-50/70",
    stripe: "bg-gradient-to-b from-amber-400 to-orange-500",
    icon: "bg-gradient-to-br from-amber-400 to-orange-500 shadow-amber-500/30",
  },
  violet: {
    wash: "bg-violet-50/70",
    stripe: "bg-gradient-to-b from-violet-500 to-purple-600",
    icon: "bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/30",
  },
};

/** Pointer-following radial wash; disabled when prefers-reduced-motion is set. */
function FluidSurface({ as: C = "div", className = "", innerClassName = "", tone = "light", children, ...rest }) {
  const ref = useRef(null);
  const reducedRef = useRef(false);
  const { onMouseMove: userMouseMove, onMouseLeave: userMouseLeave, style: userStyle, ...domRest } = rest;

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedRef.current = mq.matches;
    const onChange = () => {
      reducedRef.current = mq.matches;
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  const onMove = (e) => {
    if (reducedRef.current) return;
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const w = r.width || 1;
    const h = r.height || 1;
    const x = ((e.clientX - r.left) / w) * 100;
    const y = ((e.clientY - r.top) / h) * 100;
    el.style.setProperty("--fx", `${x}%`);
    el.style.setProperty("--fy", `${y}%`);
    userMouseMove?.(e);
  };

  const onLeave = (e) => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--fx", "50%");
    el.style.setProperty("--fy", "50%");
    userMouseLeave?.(e);
  };

  const isDark = tone === "dark";

  return (
    <C
      ref={ref}
      {...domRest}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={["group/fluid relative overflow-hidden", className].filter(Boolean).join(" ")}
      style={{
        "--fx": "50%",
        "--fy": "50%",
        ...(userStyle && typeof userStyle === "object" ? userStyle : {}),
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 ease-out motion-reduce:hidden group-hover/fluid:opacity-100"
        style={
          isDark
            ? {
                mixBlendMode: "soft-light",
                background:
                  "radial-gradient(ellipse 100% 85% at var(--fx, 50%) var(--fy, 50%), rgba(255,255,255,0.5), rgba(255,255,255,0.12) 40%, transparent 60%)",
              }
            : {
                background:
                  "radial-gradient(ellipse 120% 90% at var(--fx, 50%) var(--fy, 50%), rgba(255,255,255,0.55), rgba(255,255,255,0.08) 38%, transparent 58%)",
              }
        }
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 opacity-0 transition-opacity duration-300 ease-out motion-reduce:hidden group-hover/fluid:opacity-100"
        style={
          isDark
            ? {
                mixBlendMode: "overlay",
                background:
                  "radial-gradient(ellipse 90% 75% at var(--fx, 50%) var(--fy, 50%), rgba(255,255,255,0.22), transparent 55%)",
              }
            : {
                background:
                  "radial-gradient(ellipse 100% 80% at var(--fx, 50%) var(--fy, 50%), rgba(13,148,136,0.12), transparent 52%)",
              }
        }
      />
      <div className={["relative z-[1]", innerClassName].filter(Boolean).join(" ")}>{children}</div>
    </C>
  );
}

/** Big number card — left accent + icon */
function HeroStat({ title, value, subtitle, iconClass, accent }) {
  const a = HERO_ACCENTS[accent] || HERO_ACCENTS.blue;

  return (
    <FluidSurface
      className={`rounded-2xl border border-white/80 p-5 shadow-lg shadow-slate-200/50 ring-1 ring-slate-100/80 transition-transform duration-300 ease-out will-change-transform hover:scale-[1.02] motion-reduce:transform-none motion-reduce:hover:scale-100 ${a.wash}`}
    >
      <div
        className={`pointer-events-none absolute inset-y-4 left-0 w-1 rounded-full ${a.stripe}`}
      />
      <div className="pl-3">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm font-medium text-slate-600">{title}</p>
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-lg text-white shadow-lg ${a.icon}`}
          >
            <i className={iconClass} />
          </span>
        </div>
        <p className="mt-2 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{value}</p>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </div>
    </FluidSurface>
  );
}

/** Live inventory pill */
function LivePill({ icon, label, value, tone }) {
  const tones = {
    slate: "bg-slate-100 text-slate-800 ring-slate-200/80",
    indigo: "bg-indigo-50 text-indigo-900 ring-indigo-200/60",
    rose: "bg-rose-50 text-rose-900 ring-rose-200/60",
    cyan: "bg-cyan-50 text-cyan-900 ring-cyan-200/60",
    fuchsia: "bg-fuchsia-50 text-fuchsia-900 ring-fuchsia-200/60",
    lime: "bg-lime-50 text-lime-900 ring-lime-200/60",
  };
  return (
    <FluidSurface
      className={`flex min-w-[140px] flex-1 items-center gap-3 rounded-2xl px-4 py-3 ring-1 transition-transform duration-300 ease-out hover:scale-[1.02] motion-reduce:transform-none motion-reduce:hover:scale-100 ${tones[tone] || tones.slate}`}
    >
      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 text-lg shadow-sm ring-1 ring-black/5">
        <i className={icon} />
      </span>
      <div>
        <p className="text-xs font-medium uppercase tracking-wide opacity-70">{label}</p>
        <p className="text-xl font-bold tabular-nums">{formatInt(value)}</p>
      </div>
    </FluidSurface>
  );
}

/** One row in the “by service” list */
function ServiceRow({ name, icon, bookings, revenue, maxBookings }) {
  const pct = maxBookings > 0 ? Math.min(100, Math.round((bookings / maxBookings) * 100)) : 0;
  return (
    <div className="flex flex-col gap-2 border-b border-slate-100 py-4 last:border-0 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <i className={`${icon} text-lg`} />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">{name}</p>
          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>
      <div className="flex shrink-0 gap-8 pl-14 sm:pl-0">
        <div className="text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Bookings</p>
          <p className="text-lg font-bold tabular-nums text-slate-800">{formatInt(bookings)}</p>
        </div>
        <div className="text-right">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Paid in</p>
          <p className="text-lg font-bold tabular-nums text-emerald-700">{formatCurrency(revenue)}</p>
        </div>
      </div>
    </div>
  );
}

function RecentList({ title, rows, viewLink, emptyText, className = "" }) {
  return (
    <FluidSurface
      className={`flex h-full min-h-[260px] flex-col rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/40 ring-1 ring-slate-100 ${className}`}
    >
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white px-5 py-4">
        <h2 className="text-base font-bold text-slate-800">{title}</h2>
        <Link
          to={viewLink}
          className="shrink-0 rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700"
        >
          View all
        </Link>
      </div>
      <ul className="min-h-0 flex-1 divide-y divide-slate-100 overflow-y-auto">
        {(rows || []).length === 0 ? (
          <li className="px-5 py-10 text-center text-sm text-slate-500">{emptyText}</li>
        ) : (
          rows.map((row) => (
            <li
              key={`${row.module ?? "row"}-${row.id}`}
              className="flex flex-wrap items-center justify-between gap-2 px-5 py-3.5 hover:bg-slate-50/80"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-slate-900">{row.service_name}</p>
                <p className="text-sm text-slate-500">{row.customer_name}</p>
              </div>
              <div className="text-right">
                <p className="font-semibold text-emerald-700">{formatCurrency(row.amount)}</p>
                <p className="text-xs text-slate-400">{formatWhen(row.created_at)}</p>
              </div>
            </li>
          ))
        )}
      </ul>
    </FluidSurface>
  );
}

const Dashboard = () => {
  const [periodTab, setPeriodTab] = useState("month");

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["admin-dashboard-summary"],
    queryFn: fetchDashboardSummary,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const periods = data?.periods || {};
  const inventory = data?.inventory || {};
  const recent = data?.recent || {};
  const active = periods[periodTab] || {};

  const b = active.bookings || {};
  const rev = active.revenue_completed_payments || {};
  const pend = active.pending_payments || {};
  const mg = active.master_growth || {};

  const serviceRows = useMemo(
    () => [
      { key: "pkg", name: "Tour packages", icon: "fi fi-rr-box", bookings: b.package_bookings, revenue: rev.package },
      { key: "att", name: "Attractions", icon: "fi fi-rr-star", bookings: b.attraction_bookings, revenue: rev.attraction },
      { key: "evt", name: "Events", icon: "fi fi-rr-calendar", bookings: b.event_bookings, revenue: rev.event },
      { key: "act", name: "Activities", icon: "fi fi-rr-activity", bookings: b.activity_bookings, revenue: rev.activity },
      { key: "rent", name: "Rentals", icon: "fi fi-rr-car", bookings: b.rental_bookings, revenue: rev.rental },
    ],
    [b, rev]
  );

  const maxBookings = useMemo(
    () => Math.max(1, ...serviceRows.map((r) => Number(r.bookings) || 0)),
    [serviceRows]
  );

  const newListingsTotal = useMemo(
    () =>
      (Number(mg.packages) || 0) +
      (Number(mg.attractions) || 0) +
      (Number(mg.events) || 0) +
      (Number(mg.activities) || 0) +
      (Number(mg.rental_items) || 0) +
      (Number(mg.blogs) || 0),
    [mg]
  );

  return (
    <AdminPageLayout className="pb-12">
      <AdminPageHeader
        title={ADMIN_PAGE_META.dashboard.title}
        subtitle={ADMIN_PAGE_META.dashboard.subtitle}
        actions={
          <FluidSurface className="self-start rounded-xl shadow-lg shadow-slate-300/50">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={isFetching}
              className="relative z-[1] inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <i className={`fi fi-rr-refresh ${isFetching ? "animate-spin" : ""}`} />
              {isFetching ? "Refreshing…" : "Refresh data"}
            </button>
          </FluidSurface>
        }
      />
      {data?.generated_at ? (
        <p className="-mt-4 mb-8 text-xs text-slate-400">
          Updated {formatWhen(data.generated_at)}
          {data.timezone ? ` · ${data.timezone}` : ""}
        </p>
      ) : null}

        {isError ? (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 shadow-sm">
            {error?.message || "Could not load dashboard data."}
          </div>
        ) : null}

        {/* Live on platform */}
        <section className="mb-8">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Live on your platform</p>
          {isLoading ? (
            <div className="flex flex-wrap gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="h-16 flex-1 min-w-[120px] animate-pulse rounded-2xl bg-slate-200/80" />
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              <LivePill icon="fi fi-rr-box" label="Packages" value={inventory.packages_published} tone="indigo" />
              <LivePill icon="fi fi-rr-star" label="Attractions" value={inventory.attractions_live} tone="rose" />
              <LivePill icon="fi fi-rr-calendar" label="Events" value={inventory.events_live} tone="cyan" />
              <LivePill icon="fi fi-rr-car" label="Rentals" value={inventory.rental_items_approved} tone="fuchsia" />
              <LivePill icon="fi fi-rr-activity" label="Activities" value={inventory.activities_live} tone="lime" />
              <LivePill
                icon="fi fi-rr-users"
                label="Customers"
                value={inventory.website_users_total}
                tone="slate"
              />
              <LivePill icon="fi fi-rr-briefcase" label="Suppliers" value={inventory.suppliers_total} tone="slate" />
            </div>
          )}
        </section>

        {/* Period selector */}
        <FluidSurface
          className="mb-6 flex flex-wrap items-center gap-2 rounded-2xl border border-slate-200/80 bg-white/90 p-2 shadow-md shadow-slate-200/30 ring-1 ring-slate-100"
          innerClassName="flex flex-wrap items-center gap-2"
        >
          {PERIOD_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setPeriodTab(t.id)}
              className={`flex flex-1 flex-col items-center rounded-xl px-4 py-3 text-center transition-all sm:flex-none sm:min-w-[140px] ${
                periodTab === t.id
                  ? "bg-gradient-to-br from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-300/40"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              <span className="text-sm font-bold">{t.label}</span>
              <span className={`text-xs ${periodTab === t.id ? "text-indigo-100" : "text-slate-400"}`}>{t.hint}</span>
            </button>
          ))}
        </FluidSurface>

        {isLoading ? (
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-36 animate-pulse rounded-2xl bg-slate-200/70" />
              ))}
            </div>
            <div className="h-64 animate-pulse rounded-2xl bg-slate-200/70" />
          </div>
        ) : (
          <>
            {/* Hero KPIs — one time range */}
            <section className="mb-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <HeroStat
                title="Bookings (all services)"
                value={formatInt(b.bookings_total)}
                subtitle="New bookings recorded in this period"
                iconClass="fi fi-rr-book-alt"
                accent="blue"
              />
              <HeroStat
                title="Money collected"
                value={formatCurrency(rev.total)}
                subtitle="Completed online payments only"
                iconClass="fi fi-rr-money"
                accent="emerald"
              />
              <HeroStat
                title="Still pending"
                value={formatCurrency(pend.amount)}
                subtitle={`${formatInt(pend.count)} payment${pend.count === 1 ? "" : "s"} waiting`}
                iconClass="fi fi-rr-hourglass-end"
                accent="amber"
              />
              <HeroStat
                title="Package leads"
                value={formatInt(active.package_enquiries)}
                subtitle="Enquiry forms submitted"
                iconClass="fi fi-rr-envelope"
                accent="violet"
              />
            </section>

            {/* Secondary stats */}
            <div className="mb-8 grid gap-3 sm:grid-cols-3">
              <FluidSurface className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-md shadow-slate-200/30 ring-1 ring-slate-50 transition-transform duration-300 hover:scale-[1.01] motion-reduce:hover:scale-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">New customers</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{formatInt(active.new_website_users)}</p>
                <p className="text-sm text-slate-500">Signed up in this period</p>
              </FluidSurface>
              <FluidSurface className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-md shadow-slate-200/30 ring-1 ring-slate-50 transition-transform duration-300 hover:scale-[1.01] motion-reduce:hover:scale-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">New suppliers</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{formatInt(active.new_suppliers)}</p>
                <p className="text-sm text-slate-500">Joined in this period</p>
              </FluidSurface>
              <FluidSurface className="rounded-2xl border border-slate-200/80 bg-white px-5 py-4 shadow-md shadow-slate-200/30 ring-1 ring-slate-50 transition-transform duration-300 hover:scale-[1.01] motion-reduce:hover:scale-100">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">New listings added</p>
                <p className="mt-1 text-2xl font-bold text-slate-900">{formatInt(newListingsTotal)}</p>
                <p className="text-sm text-slate-500">Packages, events, blogs, etc.</p>
              </FluidSurface>
            </div>

            {/* By service — single clear block */}
            <section className="mb-8">
              <FluidSurface className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-xl shadow-slate-200/50 ring-1 ring-slate-100 sm:p-8">
                <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900">Split by service</h2>
                    <p className="text-sm text-slate-500">
                      Bookings vs payment volume for <strong>{PERIOD_TABS.find((x) => x.id === periodTab)?.label}</strong>
                    </p>
                  </div>
                </div>
                <div className="mt-2">
                  {serviceRows.map((row) => (
                    <ServiceRow
                      key={row.key}
                      name={row.name}
                      icon={row.icon}
                      bookings={row.bookings}
                      revenue={row.revenue}
                      maxBookings={maxBookings}
                    />
                  ))}
                </div>
              </FluidSurface>
            </section>
          </>
        )}

        {/* Recent bookings — horizontal scroll */}
        <section className="mb-10">
          <h2 className="mb-3 text-sm font-bold uppercase tracking-widest text-slate-400">Latest bookings</h2>
          <div className="relative -mx-4 px-4 sm:mx-0 sm:px-0">
            <div
              className="flex gap-4 overflow-x-auto pb-3 pt-1 scroll-smooth snap-x snap-mandatory [scrollbar-width:thin] [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 hover:[&::-webkit-scrollbar-thumb]:bg-slate-400"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <div className="w-[min(100vw-2rem,360px)] min-w-[300px] max-w-[360px] shrink-0 snap-start sm:min-w-[340px]">
                <RecentList
                  title="Latest package bookings"
                  rows={recent.package_bookings}
                  viewLink="/bookings"
                  emptyText="No package bookings yet."
                />
              </div>
              <div className="w-[min(100vw-2rem,360px)] min-w-[300px] max-w-[360px] shrink-0 snap-start sm:min-w-[340px]">
                <RecentList
                  title="Latest attraction bookings"
                  rows={recent.attraction_bookings}
                  viewLink="/bookings"
                  emptyText="No attraction bookings yet."
                />
              </div>
              <div className="w-[min(100vw-2rem,360px)] min-w-[300px] max-w-[360px] shrink-0 snap-start sm:min-w-[340px]">
                <RecentList
                  title="Latest activity bookings"
                  rows={recent.activity_bookings}
                  viewLink="/bookings"
                  emptyText="No activity bookings yet."
                />
              </div>
              <div className="w-[min(100vw-2rem,360px)] min-w-[300px] max-w-[360px] shrink-0 snap-start sm:min-w-[340px]">
                <RecentList
                  title="Latest event bookings"
                  rows={recent.event_bookings}
                  viewLink="/bookings"
                  emptyText="No event bookings yet."
                />
              </div>
              <div className="w-[min(100vw-2rem,360px)] min-w-[300px] max-w-[360px] shrink-0 snap-start sm:min-w-[340px]">
                <RecentList
                  title="Latest rental bookings"
                  rows={recent.rental_bookings}
                  viewLink="/bookings"
                  emptyText="No rental bookings yet."
                />
              </div>
            </div>
          </div>
        </section>

        {/* Quick actions */}
        <section>
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-slate-400">Shortcuts</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <FluidSurface
              as={Link}
              to="/packages"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-700 text-white shadow-xl shadow-blue-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-box pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Packages</h3>
              <p className="relative mt-1 text-sm text-blue-100">Edit tours & approvals</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/enquiry"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-fuchsia-500 to-purple-700 text-white shadow-xl shadow-fuchsia-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-envelope pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Enquiries</h3>
              <p className="relative mt-1 text-sm text-fuchsia-100">Reply to leads</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/transactions"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-700 text-white shadow-xl shadow-emerald-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-money pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Payments</h3>
              <p className="relative mt-1 text-sm text-emerald-100">All services in one list</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/supplier-activities"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-orange-500 to-rose-600 text-white shadow-xl shadow-orange-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-activity pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Activities</h3>
              <p className="relative mt-1 text-sm text-orange-100">Supplier listings & approvals</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/attractions"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-rose-500 to-pink-700 text-white shadow-xl shadow-rose-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-star pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Attractions</h3>
              <p className="relative mt-1 text-sm text-rose-100">Manage & approve listings</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/events"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-700 text-white shadow-xl shadow-sky-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-calendar pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Events</h3>
              <p className="relative mt-1 text-sm text-sky-100">Shows, pricing & approvals</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
            <FluidSurface
              as={Link}
              to="/rentals"
              tone="dark"
              className="group/card relative overflow-hidden rounded-2xl bg-gradient-to-br from-amber-500 to-orange-800 text-white shadow-xl shadow-amber-400/25 transition hover:scale-[1.02] hover:shadow-2xl motion-reduce:transform-none motion-reduce:hover:scale-100"
              innerClassName="relative block min-h-[180px] p-6"
            >
              <i className="fi fi-rr-car pointer-events-none absolute -right-2 -bottom-2 text-8xl opacity-20 transition group-hover/card:opacity-30" />
              <h3 className="relative text-lg font-bold">Rentals</h3>
              <p className="relative mt-1 text-sm text-amber-100">Fleet items & approvals</p>
              <span className="relative mt-4 inline-flex items-center text-sm font-semibold">
                Open <i className="fi fi-rr-arrow-right ml-1 text-xs" />
              </span>
            </FluidSurface>
          </div>
        </section>
    </AdminPageLayout>
  );
};

export default Dashboard;
